import type { RootState } from "../store";
import type { FDItemType } from "../../../api/src/types";
import {
  buildSerializedAnswerMutationHeaders,
  type Dispatch,
  patchQueryDataIfPresent,
} from "./answerMutationHelpers";
import type { FinancialDisclosureCollectionsResponse } from "../../../api/src/users/financial-disclosures/route.js";
import { allFDItemsHaveRequiredFields } from "../../../api/src/utils/fd-completeness";
import { hasAllRequiredIncomeYears } from "../../../api/src/utils/income-years";
import {
  clearDocumentApprovalsAndPDFsPlanIsNoOp,
  planClearDocumentApprovalsAndPDFs,
} from "../../../api/src/users/financial-disclosures/utils/clear-document-approvals-and-pdfs-plan";
import { fdFieldValuesEqual } from "../../../api/src/users/financial-disclosures/utils/fd-upsert-noop";
import { computeUserApprovalsBreakdown } from "../../../api/src/users/financial-disclosures/utils/fd-approval";
import {
  DocumentTemplateType,
  FinancialDisclosureUpsertMutationKind,
} from "../../../api/src/types";
import { recomputeDocumentApprovalState } from "../../../api/src/documents/utils/document-approval-state";
import { isPendingManualNotarization } from "../../../api/src/documents/notarizations/lifecycle";
import { isPostnup } from "../utils/branding";
import { documentsApi } from "./documents";
import {
  financialDisclosuresApi,
  type FinancialDisclosureCachedCollectionItem,
  type FinancialDisclosureOptimisticFieldMarker,
  type FinancialDisclosureOptimisticCacheFields,
  type BasePathParams,
  type FinancialDisclosureCollectionKey,
  type FinancialDisclosureUpsertCollectionKey,
  type FinancialDisclosureRequiredUserFields,
  type FinancialDisclosureUpdatePayload,
  type PerTypeApprovalMutationArgs,
  getFDStatusTag,
  getFinancialDisclosuresTag,
  selectFDStatusQueryState,
  selectFinancialDisclosureCollectionQueryState,
  updateFDStatusQueryData,
  updateFinancialDisclosureCollectionQueryData,
} from "./financials/financialDisclosures";
import type {
  FDUpsertableType,
  FDUpsertResponseMap,
} from "../pages/financials/common/fdUtils";

export interface FinancialDisclosureEntity {
  id: string;
  updatedAt?: string;
  draftKey?: string;
  userId: string;
  partnerApprovedAt?: string | null; // optional because of legacy joint - might be cleaner to add it to even if unused just so this typing is stricter
  selfApprovedAt?: string | null;
}

export type FinancialDisclosureTarget =
  | {
      itemId: string;
      draftKey?: string;
    }
  | {
      itemId?: string;
      draftKey: string;
    };

type FDCollectionItem<TKey extends FinancialDisclosureCollectionKey> =
  FinancialDisclosureCollectionsResponse[TKey][number];

// Per-type maps for in-flight FD creates. Segregating by FDUpsertableType
// gives each map a fully typed Promise<FDUpsertResponseMap[K]>, so no cast is
// needed at the read site and there is no cross-type slot collision at runtime.
type InFlightMap = {
  [K in FDUpsertableType]: Map<string, Promise<FDUpsertResponseMap[K]>>;
};
const inFlightFinancialDisclosureCreates: Partial<InFlightMap> = {};
let fdDraftCounter = 0;

export const financialDisclosureResourcePaths: Record<FDItemType, string> = {
  ACCOUNTS: "accounts",
  CHILD_ACCOUNTS: "child-accounts",
  ASSETS: "assets",
  VEHICLES: "vehicles",
  INCOME: "income",
  REAL_ESTATE: "real-estate",
  BUSINESS: "business",
  DEBTS: "debts",
  INHERITANCE: "inheritance",
  TRUSTS: "trusts",
  LEGACY_JOINT: "legacy-joint",
};

export function matchesFinancialDisclosureEntity(
  entity: FinancialDisclosureEntity,
  params: FinancialDisclosureTarget,
): boolean {
  const { itemId, draftKey } = params;

  if (itemId && entity.id === itemId) {
    return true;
  }

  if (draftKey == null) {
    return false;
  }

  return entity.id === draftKey || entity.draftKey === draftKey;
}

export function requireFinancialDisclosureTarget(params: {
  itemId?: string | null | undefined;
  draftKey?: string | null | undefined;
}): FinancialDisclosureTarget {
  const { itemId, draftKey } = params;

  if (draftKey) {
    return { draftKey, ...(itemId ? { itemId } : {}) };
  }

  if (itemId) {
    return { itemId };
  }

  throw new Error("Financial disclosure target requires itemId or draftKey.");
}

/**
 * Re-derives the `users[ownerId]` slot in the cached fdStatus from the
 * optimistically-patched aggregate. Returns the patch handle for rollback.
 */
export function patchFDStatusUserSlotFromAggregate(params: {
  dispatch: Dispatch;
  getState: () => unknown;
  aggregateQueryArgs: BasePathParams;
  statusQueryArgs: BasePathParams;
}): { undo: () => void } | null {
  const { dispatch, getState, aggregateQueryArgs, statusQueryArgs } = params;
  const state = getState() as RootState;
  const aggregate =
    financialDisclosuresApi.endpoints.getFinancialDisclosuresByUserId.select(
      aggregateQueryArgs,
    )(state).data;
  const statusState = selectFDStatusQueryState(state, statusQueryArgs);
  // No patch possible until both caches have populated.
  if (!aggregate || !statusState.data) return null;

  const slotUserId = aggregateQueryArgs.userId;
  const cachedSlot = statusState.data.users[slotUserId];
  const derivedIncomeYears = hasAllRequiredIncomeYears(aggregate.incomes);
  const derivedFields = allFDItemsHaveRequiredFields(aggregate);
  // Postnup: re-derive the split flags from the aggregate so the actor-
  // actionable distinction in `resolveAudienceFromBackendFDStatus`
  // stays consistent with the server between mutation and refetch.
  // Prenup: preserve the cached values (server always returns `true`s).
  const derivedBreakdown = isPostnup
    ? computeUserApprovalsBreakdown(
        aggregate,
        slotUserId,
        // This branch only runs on the postnup branding build, so the
        // doc type is statically POSTNUP. (Per-doc-type joint-capability
        // classification of inheritance/trust lives in
        // `computeUserApprovalsBreakdown`.)
        DocumentTemplateType.POSTNUP,
      )
    : {
        hasAllPartnerApprovals: cachedSlot?.hasAllPartnerApprovals ?? true,
        hasAllSelfApprovals: cachedSlot?.hasAllSelfApprovals ?? true,
      };
  const derivedApprovals =
    derivedBreakdown.hasAllPartnerApprovals &&
    derivedBreakdown.hasAllSelfApprovals;
  // Skip dispatch if the cache already reflects the derived values
  // (avoids noise undo entries on rollback).
  if (
    cachedSlot?.hasAllRequiredIncomeYears === derivedIncomeYears &&
    cachedSlot.hasAllRequiredFields === derivedFields &&
    cachedSlot.hasAllPartnerApprovals ===
      derivedBreakdown.hasAllPartnerApprovals &&
    cachedSlot.hasAllSelfApprovals === derivedBreakdown.hasAllSelfApprovals &&
    cachedSlot.hasAllApprovals === derivedApprovals
  ) {
    return null;
  }

  const patch = dispatch(
    updateFDStatusQueryData(statusQueryArgs, (draft) => {
      const slot = draft.users[slotUserId];
      if (!slot) return;
      slot.hasAllRequiredIncomeYears = derivedIncomeYears;
      slot.hasAllRequiredFields = derivedFields;
      slot.hasAllPartnerApprovals = derivedBreakdown.hasAllPartnerApprovals;
      slot.hasAllSelfApprovals = derivedBreakdown.hasAllSelfApprovals;
      slot.hasAllApprovals = derivedApprovals;
      // Mirror the server's `isComplete` predicate: requires both spouses
      // present (status only contains user A's slot pre-pairing) and every
      // slot satisfied. Without the two-slot guard, optimistically toggling
      // user A to fully complete on an unpaired doc would briefly mark the
      // whole status complete — diverging from the server until refetch.
      const slots = Object.values(draft.users);
      draft.isComplete =
        slots.length >= 2 &&
        slots.every(
          (s) =>
            s.hasAllRequiredIncomeYears &&
            s.hasAllRequiredFields &&
            s.hasAllApprovals,
        );
    }),
  );
  return patch;
}

/** Convenience wrapper for self-edits where aggregate and status share `queryArgs`. */
export function patchOwnFDStatusFromAggregate(params: {
  dispatch: Dispatch;
  getState: () => unknown;
  queryArgs: BasePathParams;
}): { undo: () => void } | null {
  return patchFDStatusUserSlotFromAggregate({
    dispatch: params.dispatch,
    getState: params.getState,
    aggregateQueryArgs: params.queryArgs,
    statusQueryArgs: params.queryArgs,
  });
}

/**
 * Refreshes document and notarization state after successful changes.
 * Failed or no-op writes refresh a patched document instead of inverse-patching.
 * `.patched` exposes whether the cache was modified (for tests).
 */
export type FinalizeDocPatch = ((
  reason: "success" | "server-noop" | "error",
) => void) & { patched: boolean };

/** Optimistically mirrors the backend document-artifact clear in the `getDocument` cache. */
export function patchDocumentClearApprovalsAndPDFs(params: {
  dispatch: Dispatch;
  getState: () => unknown;
  documentId: string;
}): FinalizeDocPatch {
  const { dispatch, getState, documentId } = params;
  const state = getState() as RootState;
  const cached =
    documentsApi.endpoints.getDocument.select(documentId)(state).data;
  const makeFinalize = (patched: boolean): FinalizeDocPatch => {
    const fn = (reason: "success" | "server-noop" | "error") => {
      if (reason === "success")
        dispatch(
          documentsApi.util.invalidateTags(["documents", "notarizations"]),
        );
      else if (patched)
        dispatch(documentsApi.util.invalidateTags(["documents"]));
    };
    return Object.assign(fn, { patched });
  };
  // No cached doc → nothing in cache to drift; next subscriber fetches fresh.
  if (!cached) return makeFinalize(false);

  const hasActiveOrCompletedNotarization = (
    documentsApi.endpoints.getNotarizationsByDocumentId.select(documentId)(
      state,
    ).data ?? []
  ).some((n) => n.deletedAt == null && !isPendingManualNotarization(n));

  const plan = planClearDocumentApprovalsAndPDFs({
    ...cached,
    hasActiveOrCompletedNotarization,
  });

  if (clearDocumentApprovalsAndPDFsPlanIsNoOp(plan)) return makeFinalize(false);

  const {
    hasAttorneyApprovalsToClear,
    hasCachedPdfsToClear,
    hasReviewedAtToClear,
  } = plan;

  dispatch(
    documentsApi.util.updateQueryData("getDocument", documentId, (draft) => {
      if (hasAttorneyApprovalsToClear) {
        // Mirrors backend `clearDocumentApprovalsAndPDFs` via the same
        // shared invariant helper.
        draft.attorneyAApprovedAtPrior = draft.attorneyAApprovedAt ?? null;
        draft.attorneyBApprovedAtPrior = draft.attorneyBApprovedAt ?? null;
        // Cache stores ISO strings on the wire; coerce to `Date` for the helper.
        const recomputed = recomputeDocumentApprovalState(
          {
            attorneyAId: draft.attorneyAId,
            attorneyBId: draft.attorneyBId,
            attorneyAApprovedAt: draft.attorneyAApprovedAt
              ? new Date(draft.attorneyAApprovedAt)
              : null,
            attorneyBApprovedAt: draft.attorneyBApprovedAt
              ? new Date(draft.attorneyBApprovedAt)
              : null,
            status: draft.status,
            finalizedAt: draft.finalizedAt ? new Date(draft.finalizedAt) : null,
            finalizedAtPrior: draft.finalizedAtPrior
              ? new Date(draft.finalizedAtPrior)
              : null,
          },
          {
            attorneyAId: draft.attorneyAId,
            attorneyBId: draft.attorneyBId,
            attorneyAApprovedAt: null,
            attorneyBApprovedAt: null,
          },
        );
        draft.attorneyAApprovedAt = null;
        draft.attorneyBApprovedAt = null;
        draft.status = recomputed.status;
        draft.statusModifiedAt = recomputed.statusModifiedAt;
        draft.finalizedAt = recomputed.finalizedAt;
        draft.finalizedAtPrior = recomputed.finalizedAtPrior;
      }
      if (hasCachedPdfsToClear) {
        draft.generatedPdfFileId = null;
        draft.proofFileId = null;
      }
      if (hasReviewedAtToClear) {
        draft.reviewedAt = null;
      }
    }),
  );

  return makeFinalize(true);
}

export const financialDisclosureKeys = {
  mutation(
    params: {
      documentId: string;
      resourcePath: string;
    } & FinancialDisclosureTarget,
  ): string {
    const { documentId, resourcePath, ...target } = params;

    return `${documentId}:${resourcePath}:${target.draftKey ?? target.itemId}`;
  },

  questionGroups(
    params: {
      userId: string;
      docId: string;
      fdItemType: FDItemType;
    } & FinancialDisclosureTarget,
  ): string {
    const { userId, docId, fdItemType, ...target } = params;

    return `${userId}:${docId}:${fdItemType}:${target.draftKey ?? target.itemId}`;
  },

  draft(resourcePath: string): string {
    fdDraftCounter += 1;
    return `draft:${resourcePath}:${Date.now()}:${fdDraftCounter}`;
  },
};

export function buildSerializedFinancialDisclosureHeaders(
  params: {
    documentId: string;
    resourcePath: string;
    pruneBy?: string | null | undefined;
    serializeDebounceMs?: number | undefined;
  } & FinancialDisclosureTarget,
) {
  const { documentId, resourcePath, pruneBy, serializeDebounceMs, ...target } =
    params;

  const serializeBy = financialDisclosureKeys.mutation({
    documentId,
    resourcePath,
    ...target,
  });

  return buildSerializedAnswerMutationHeaders({
    serializeBy,
    pruneBy:
      pruneBy === undefined ? (target.draftKey ?? target.itemId) : pruneBy,
    serializeDebounceMs,
  });
}

export function hasInFlightFinancialDisclosureCreate(
  fdType: FDUpsertableType,
  key: string,
): boolean {
  return inFlightFinancialDisclosureCreates[fdType]?.has(key) ?? false;
}

export async function getOrStartFinancialDisclosureCreate<
  TType extends FDUpsertableType,
>(params: {
  fdType: TType;
  key: string;
  create: () => Promise<FDUpsertResponseMap[TType]>;
}): Promise<FDUpsertResponseMap[TType]> {
  const { fdType, key, create } = params;

  let typeMap = inFlightFinancialDisclosureCreates[fdType];
  if (!typeMap) {
    typeMap = new Map();
    inFlightFinancialDisclosureCreates[fdType] = typeMap;
  }

  const existing = typeMap.get(key);
  if (existing) {
    return existing;
  }

  const createPromise = create().finally(() => {
    if (
      inFlightFinancialDisclosureCreates[fdType]?.get(key) === createPromise
    ) {
      inFlightFinancialDisclosureCreates[fdType].delete(key);
    }
  });

  typeMap.set(key, createPromise);
  return createPromise;
}

export async function runFinancialDisclosureDeleteMutation(params: {
  dispatch: Dispatch;
  getState: () => unknown;
  queryFulfilled: Promise<unknown>; // we don't use the response here, so unknown is fine but should fix later
  itemId: string;
  queryArgs: BasePathParams;
  collectionKey: FinancialDisclosureCollectionKey;
}): Promise<void> {
  const {
    dispatch,
    getState,
    queryFulfilled,
    itemId,
    queryArgs,
    collectionKey,
  } = params;

  // Mirror the server-side `clearDocumentApprovalsAndPDFs` triggered
  // by an FD delete so the cached doc reflects the new (unapproved) state
  // immediately.
  const optimisticDocPatch = patchDocumentClearApprovalsAndPDFs({
    dispatch,
    getState,
    documentId: queryArgs.docId,
  });

  try {
    await queryFulfilled;
    dispatch(
      updateFinancialDisclosureCollectionQueryData(
        queryArgs,
        collectionKey,
        (draft) => {
          const existingIndex = draft.findIndex((item) => item.id === itemId);
          if (existingIndex === -1) {
            return;
          }

          draft.splice(existingIndex, 1);
        },
      ),
    );
    // Re-derive FD status flags from the post-delete aggregate so
    // completeness consumers reflect the change before the refetch lands.
    patchOwnFDStatusFromAggregate({ dispatch, getState, queryArgs });
    dispatch(
      financialDisclosuresApi.util.invalidateTags([
        getFDStatusTag(queryArgs.docId),
      ]),
    );
    optimisticDocPatch("success");
  } catch {
    optimisticDocPatch("error");
    dispatch(
      financialDisclosuresApi.util.invalidateTags([
        getFinancialDisclosuresTag(queryArgs),
      ]),
    );
  }
}

// `mutationKind` is declared as an optional field on the cached row type (see
// `FinancialDisclosureOptimisticCacheFields`), so the response type below is
// a structural subtype of `FinancialDisclosureCachedCollectionItem<TKey>`.
// That subtype relationship is what lets us write the response object
// straight into the cache below with no cast (just an inline `draftKey`
// merge for the optimistic-create path).
type FinancialDisclosureUpsertResponseForCache<
  TKey extends FinancialDisclosureUpsertCollectionKey,
> = FinancialDisclosureCachedCollectionItem<TKey> & {
  mutationKind: FinancialDisclosureUpsertMutationKind;
};

export async function runFinancialDisclosureUpsertMutation<
  TKey extends FinancialDisclosureUpsertCollectionKey,
>(params: {
  dispatch: Dispatch;
  getState: () => unknown;
  // Callers pass their typed queryFulfilled (e.g. Promise<{ data: IncomesUpsertResponse }>).
  // The upsert response is widened to the cached item shape here: required user
  // fields, stamped system fields, plus optional frontend cache fields and the
  // server-side `mutationKind` discriminator. This will fail if there is a
  // structural mismatch between the upsert response and the cached item shape.
  queryFulfilled: Promise<{
    data: FinancialDisclosureUpsertResponseForCache<TKey>;
  }>;
  queryArgs: BasePathParams;
  collectionKey: TKey;
  target: FinancialDisclosureTarget;
  optimisticUpdate?:
    | FinancialDisclosureUpdatePayload<FDCollectionItem<TKey>>
    | undefined;
  // Seed values for required non-nullable user fields (e.g. `isJoint` on
  // Vehicle). Threaded here so the optimistic-create builder can stamp them
  // onto the cached row with a type-checked spread rather than relying on the
  // loose `optimisticUpdate` Record. Unused on the update path.
  createDefaults: FinancialDisclosureRequiredUserFields<TKey>;
  rtkEditId?: string | undefined;
}): Promise<void> {
  const {
    dispatch,
    getState,
    queryFulfilled,
    queryArgs,
    collectionKey,
    target,
    optimisticUpdate,
    createDefaults,
    rtkEditId,
  } = params;

  const { itemId, draftKey } = target;
  const effectiveRtkEditId =
    rtkEditId ??
    `${collectionKey}:${draftKey ?? itemId ?? "unknown"}:${Date.now()}`;
  const selectQueryState = (state: RootState, args: BasePathParams) =>
    selectFinancialDisclosureCollectionQueryState(state, args, collectionKey);
  const updateQueryData = (
    args: BasePathParams,
    updater: (draft: FinancialDisclosureCachedCollectionItem<TKey>[]) => void,
  ) =>
    updateFinancialDisclosureCollectionQueryData(args, collectionKey, updater);
  const affectedFieldNames = getAffectedFieldNames(optimisticUpdate);
  const optimisticMarker: FinancialDisclosureOptimisticFieldMarker | null =
    optimisticUpdate
      ? {
          rtkEditId: effectiveRtkEditId,
          updatedAt: new Date().toISOString(),
        }
      : null;
  const optimisticPatch = optimisticUpdate
    ? patchQueryDataIfPresent({
        dispatch,
        state: getState() as RootState,
        args: queryArgs,
        selectQueryState,
        updateQueryData,
        updater: (draft) => {
          const existingItem = draft.find((item) =>
            matchesFinancialDisclosureEntity(item, target),
          );
          if (!existingItem) {
            draft.push(
              createOptimisticFinancialDisclosureEntity<TKey>({
                target,
                optimisticUpdate,
                createDefaults,
                optimisticMarker,
                affectedFieldNames,
                userId: queryArgs.userId,
                documentId: queryArgs.docId,
              }),
            );
            return;
          }

          // Client-side no-op detection (mirrors server `isFDItemUpdateNoOp`):
          // skip approval nulling + markers; server agrees with `mutationKind: "no-op"`.
          if (
            affectedFieldNames.every((fieldName) =>
              fdFieldValuesEqual(
                Reflect.get(optimisticUpdate, fieldName),
                Reflect.get(existingItem, fieldName),
              ),
            )
          ) {
            return;
          }

          Object.assign(existingItem, optimisticUpdate, {
            partnerApprovedAt: null,
            selfApprovedAt: null,
          });
          markOptimisticFields(
            existingItem,
            affectedFieldNames,
            optimisticMarker,
          );
        },
      })
    : null;
  const optimisticStatusPatch = optimisticPatch
    ? patchOwnFDStatusFromAggregate({ dispatch, getState, queryArgs })
    : null;
  const optimisticDocPatch = patchDocumentClearApprovalsAndPDFs({
    dispatch,
    getState,
    documentId: queryArgs.docId,
  });

  try {
    const { data } = await queryFulfilled;
    const serverConfirmedNoOp =
      data.mutationKind === FinancialDisclosureUpsertMutationKind.NoOp;

    patchQueryDataIfPresent({
      dispatch,
      state: getState() as RootState,
      args: queryArgs,
      selectQueryState,
      updateQueryData,
      updater: (draft) => {
        const existingIndex = draft.findIndex((item) =>
          matchesFinancialDisclosureEntity(item, target),
        );
        const newRow: FinancialDisclosureCachedCollectionItem<TKey> = draftKey
          ? { ...data, draftKey }
          : data;
        if (existingIndex === -1) {
          draft.push(newRow);
          return;
        }

        const existingItem = draft[existingIndex];
        if (!existingItem) {
          draft.push(newRow);
          return;
        }

        // Narrow merge: only settle server-authoritative system fields
        // (id/updatedAt/approvals); user-editable fields already match.
        existingItem.id = data.id;
        existingItem.updatedAt = data.updatedAt;
        existingItem.partnerApprovedAt = data.partnerApprovedAt;
        existingItem.selfApprovedAt = data.selfApprovedAt;

        if (draftKey && itemId == null) {
          existingItem.draftKey = draftKey;
        }
        clearOptimisticFields(
          existingItem,
          affectedFieldNames,
          effectiveRtkEditId,
        );
      },
    });

    patchOwnFDStatusFromAggregate({ dispatch, getState, queryArgs });
    dispatch(
      financialDisclosuresApi.util.invalidateTags([
        getFDStatusTag(queryArgs.docId),
      ]),
    );
    optimisticDocPatch(serverConfirmedNoOp ? "server-noop" : "success");
  } catch {
    const queryState = selectQueryState(getState() as RootState, queryArgs);
    const existingItem = queryState.data?.find((item) =>
      matchesFinancialDisclosureEntity(item, target),
    );
    const shouldUndo =
      optimisticPatch != null &&
      existingItem != null &&
      hasOptimisticFieldMarkers(
        existingItem,
        affectedFieldNames,
        effectiveRtkEditId,
      );
    if (shouldUndo) {
      optimisticPatch.undo();
    }
    // fdStatus is a pure derivative of this aggregate mutation, so undo it.
    optimisticStatusPatch?.undo();
    optimisticDocPatch("error");
    dispatch(
      financialDisclosuresApi.util.invalidateTags([
        getFinancialDisclosuresTag(queryArgs),
        getFDStatusTag(queryArgs.docId),
      ]),
    );
  }
}

function createOptimisticFinancialDisclosureEntity<
  TKey extends FinancialDisclosureUpsertCollectionKey,
>(params: {
  target: FinancialDisclosureTarget;
  optimisticUpdate: Record<string, unknown>;
  createDefaults: FinancialDisclosureRequiredUserFields<TKey>;
  optimisticMarker: FinancialDisclosureOptimisticFieldMarker | null;
  affectedFieldNames: string[];
  userId: string;
  documentId: string;
}): FinancialDisclosureCachedCollectionItem<TKey> {
  const {
    target,
    optimisticUpdate,
    createDefaults,
    optimisticMarker,
    affectedFieldNames,
    userId,
    documentId,
  } = params;
  const id = target.itemId ?? target.draftKey;

  if (!id) {
    throw new Error(
      "Financial disclosure optimistic update requires itemId or draftKey.",
    );
  }

  const now = optimisticMarker?.updatedAt ?? new Date().toISOString();
  const entity = {
    ...createDefaults,
    ...optimisticUpdate,
    id,
    userId,
    documentId,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    createdById: userId,
    updatedById: userId,
    partnerApprovedAt: null,
    selfApprovedAt: null,
    ...(target.draftKey ? { draftKey: target.draftKey } : {}),
    ...(optimisticMarker && affectedFieldNames.length > 0
      ? {
          __optimisticFields: Object.fromEntries(
            affectedFieldNames.map((fieldName) => [
              fieldName,
              optimisticMarker,
            ]),
          ),
        }
      : {}),
  } satisfies FinancialDisclosureCachedCollectionItem<TKey>;
  return entity;
}

function getAffectedFieldNames(
  optimisticUpdate: Record<string, unknown> | undefined,
): string[] {
  if (!optimisticUpdate) {
    return [];
  }

  return Object.entries(optimisticUpdate)
    .filter(([fieldName, value]) => fieldName !== "id" && value !== undefined)
    .map(([fieldName]) => fieldName);
}

// Any cached FD item carries optional __optimisticFields; the type alias keeps
// the helper signatures readable without re-deriving the intersection.
type CachedFDEntity = FinancialDisclosureEntity & {
  __optimisticFields?: Partial<
    Record<string, FinancialDisclosureOptimisticFieldMarker>
  >;
};

function markOptimisticFields(
  entity: CachedFDEntity,
  fieldNames: string[],
  marker: FinancialDisclosureOptimisticFieldMarker | null,
): void {
  if (!marker || fieldNames.length === 0) {
    return;
  }

  const nextFields = { ...(entity.__optimisticFields ?? {}) };
  fieldNames.forEach((fieldName) => {
    nextFields[fieldName] = marker;
  });
  entity.__optimisticFields = nextFields;
}

export function clearOptimisticFields(
  entity: CachedFDEntity,
  fieldNames: string[],
  rtkEditId: string,
): void {
  if (!entity.__optimisticFields || fieldNames.length === 0) {
    return;
  }

  const removedFieldNames = new Set(fieldNames);
  const nextFields = Object.fromEntries(
    Object.entries(entity.__optimisticFields).filter(
      ([fieldName, marker]) =>
        !(removedFieldNames.has(fieldName) && marker?.rtkEditId === rtkEditId),
    ),
  );

  if (Object.keys(nextFields).length === 0) {
    delete entity.__optimisticFields;
    return;
  }

  entity.__optimisticFields = nextFields;
}

export function hasOptimisticFieldMarkers(
  entity: CachedFDEntity,
  fieldNames: string[],
  rtkEditId: string,
): boolean {
  if (!entity.__optimisticFields || fieldNames.length === 0) {
    return false;
  }

  return fieldNames.every(
    (fieldName) =>
      entity.__optimisticFields?.[fieldName]?.rtkEditId === rtkEditId,
  );
}

type ApprovalOptimisticFieldName = "partnerApprovedAt" | "selfApprovedAt";

type FinancialDisclosureApprovalCachedEntity = FinancialDisclosureEntity &
  FinancialDisclosureOptimisticCacheFields & {
    userId?: string;
    partnerApprovedAt?: string | null;
    selfApprovedAt?: string | null;
  };

export function hasApprovalOptimisticFieldMarkers(
  entity: FinancialDisclosureApprovalCachedEntity,
  fieldNames: ApprovalOptimisticFieldName[],
  rtkEditId: string,
): boolean {
  return hasOptimisticFieldMarkers(entity, fieldNames, rtkEditId);
}

function getApprovalFieldName(
  entity: Pick<FinancialDisclosureApprovalCachedEntity, "userId">,
  currentUserId: string,
): ApprovalOptimisticFieldName {
  return entity.userId === currentUserId
    ? "selfApprovedAt"
    : "partnerApprovedAt";
}

function shouldUndoApprovalOptimisticPatch(params: {
  state: RootState;
  args: PerTypeApprovalMutationArgs;
  collectionKey: FinancialDisclosureCollectionKey;
  rtkEditId: string;
}): boolean {
  const { state, args, collectionKey, rtkEditId } = params;
  const queryState = selectFinancialDisclosureCollectionQueryState(
    state,
    { userId: args.userId, docId: args.docId },
    collectionKey,
  );
  const existingItem = queryState.data?.find((item) => item.id === args.id);
  if (!existingItem) {
    return false;
  }

  return hasApprovalOptimisticFieldMarkers(
    existingItem,
    [getApprovalFieldName(existingItem, args.currentUserId)],
    rtkEditId,
  );
}

function applyApprovalOptimisticPatch(params: {
  draft: (FinancialDisclosureEntity &
    FinancialDisclosureOptimisticCacheFields)[];
  itemId: string;
  currentUserId: string;
  approvedAt: string | null;
  marker: FinancialDisclosureOptimisticFieldMarker;
}): void {
  const { draft, itemId, currentUserId, approvedAt, marker } = params;
  const existingItem = draft.find((item) => item.id === itemId);
  if (!existingItem) {
    return;
  }

  const fieldName = getApprovalFieldName(existingItem, currentUserId);
  existingItem[fieldName] = approvedAt;
  markOptimisticFields(existingItem, [fieldName], marker);
}

export function reconcileApprovalMutationSuccess(params: {
  draft: (FinancialDisclosureEntity &
    FinancialDisclosureOptimisticCacheFields)[];
  itemId: string;
  updatedItem: { id: string; updatedAt: string };
  currentUserId: string;
  rtkEditId: string;
}): void {
  const { draft, itemId, updatedItem, currentUserId, rtkEditId } = params;
  const existingItem = draft.find((item) => item.id === itemId);
  if (!existingItem) {
    return;
  }

  existingItem.id = updatedItem.id;
  existingItem.updatedAt = updatedItem.updatedAt;
  clearOptimisticFields(
    existingItem,
    [getApprovalFieldName(existingItem, currentUserId)],
    rtkEditId,
  );
}

export async function runFDApprovalMutation(params: {
  dispatch: Dispatch;
  queryFulfilled: Promise<{ data: { id: string; updatedAt: string } }>;
  getState: () => unknown;
  args: PerTypeApprovalMutationArgs;
  collectionKey: FinancialDisclosureCollectionKey;
  resourcePath: string;
  action: "approve" | "unapprove";
}): Promise<void> {
  const {
    dispatch,
    queryFulfilled,
    getState,
    args,
    collectionKey,
    resourcePath,
    action,
  } = params;
  const optimisticMarker: FinancialDisclosureOptimisticFieldMarker = {
    rtkEditId: `${args.docId}:${resourcePath}:${args.id}:${action}`,
    updatedAt: new Date().toISOString(),
  };
  const patchResult = dispatch(
    updateFinancialDisclosureCollectionQueryData(
      { userId: args.userId, docId: args.docId },
      collectionKey,
      (draft) => {
        applyApprovalOptimisticPatch({
          draft,
          itemId: args.id,
          currentUserId: args.currentUserId,
          approvedAt: action === "approve" ? optimisticMarker.updatedAt : null,
          marker: optimisticMarker,
        });
      },
    ),
  );
  // Patch the item owner's slot inside the current user's fdStatus cache
  // (they differ when approving a partner's item) so gating updates without
  // waiting for the refetch.
  const optimisticStatusPatch = patchFDStatusUserSlotFromAggregate({
    dispatch,
    getState,
    aggregateQueryArgs: { userId: args.userId, docId: args.docId },
    statusQueryArgs: { userId: args.currentUserId, docId: args.docId },
  });

  try {
    const { data } = await queryFulfilled;
    dispatch(
      updateFinancialDisclosureCollectionQueryData(
        { userId: args.userId, docId: args.docId },
        collectionKey,
        (draft) => {
          reconcileApprovalMutationSuccess({
            draft,
            itemId: args.id,
            updatedItem: data,
            currentUserId: args.currentUserId,
            rtkEditId: optimisticMarker.rtkEditId,
          });
        },
      ),
    );
  } catch {
    if (
      shouldUndoApprovalOptimisticPatch({
        state: getState() as RootState,
        args,
        collectionKey,
        rtkEditId: optimisticMarker.rtkEditId,
      })
    ) {
      patchResult.undo();
    }
    // See upsert catch path for why fdStatus is unconditionally undone.
    optimisticStatusPatch?.undo();
    dispatch(
      financialDisclosuresApi.util.invalidateTags([
        getFinancialDisclosuresTag({
          userId: args.userId,
          docId: args.docId,
        }),
        getFDStatusTag(args.docId),
      ]),
    );
  }
}
